import DossierStatus from '@/assets/enums/dossier/DossierStatus.js'

/**
 * Filter entry of an analytics dashboard, as sent to the API. Persisted dashboards
 * carry every condition of the backend filter enum, not only the ones built here.
 */
export type AnalyticsFilter = {
  column: string
  values: unknown
  condition: string
  whereType: string
}

/** Dashboard fields read when building its panel filters. */
type FilterableDashboard = {
  is_default?: boolean | number | null
  filters?: AnalyticsFilter[] | null
}

/**
 * The responsable filter of the statistics panel is a common filter: the backend
 * routes it to every element of the dashboard (dossier, event, invoicing, articles),
 * which a group-namespaced column could not do on a mixed "Général" dashboard (BBSW-9775).
 */
export const RESPONSABLE_FILTER_COLUMN = 'common#responsable'

/** Columns written by the panel before BBSW-9775, still found in persisted view states. */
export const LEGACY_RESPONSABLE_COLUMNS = [
  'invoicing#responsable',
  'dossier#responsable',
  'event#responsable'
]

/** Dossier status filter of the statistics panel, matched through the dossier of each element (BBSW-10888). */
export const DOSSIER_STATUS_FILTER_COLUMN = 'common#dossier_status'

/** Statuses left out of the predefined statistics pages until the user includes them. */
export const PREDEFINED_EXCLUDED_DOSSIER_STATUSES: string[] = [
  DossierStatus.PROSPECT,
  DossierStatus.CANCELED
]

/**
 * Rewrites legacy responsable entries onto the common column, merging their values.
 */
export function normalizeResponsableFilters(
  filters: AnalyticsFilter[] | null | undefined
): AnalyticsFilter[] {
  const responsableColumns = [
    RESPONSABLE_FILTER_COLUMN,
    ...LEGACY_RESPONSABLE_COLUMNS
  ]
  const entries = filters ?? []
  const responsableEntries = entries.filter((entry) =>
    responsableColumns.includes(entry.column)
  )

  if (responsableEntries.length === 0) {
    return [...entries]
  }

  const values = [
    ...new Set(
      responsableEntries.flatMap((entry) =>
        Array.isArray(entry.values) ? entry.values : []
      )
    )
  ]

  return [
    ...entries.filter((entry) => !responsableColumns.includes(entry.column)),
    {
      column: RESPONSABLE_FILTER_COLUMN,
      values,
      condition: 'in',
      whereType: 'where'
    }
  ]
}

function defaultDossierStatusFilter(): AnalyticsFilter {
  return {
    column: DOSSIER_STATUS_FILTER_COLUMN,
    values: [...PREDEFINED_EXCLUDED_DOSSIER_STATUSES],
    condition: 'not_in',
    whereType: 'where'
  }
}

/**
 * Initial panel filters of a dashboard. A predefined statistics page is shared by
 * every user: panel choices (responsable, dossier status) that a save may have
 * written into the template are per-user and ignored, and the page always starts
 * without prospects and cancelled dossiers. A custom dashboard keeps its filters.
 */
export function buildInitialFilters(
  dashboard: FilterableDashboard | null | undefined
): AnalyticsFilter[] {
  const filters = [...(dashboard?.filters ?? [])]

  if (!dashboard?.is_default) {
    return filters
  }

  const panelColumns = [
    RESPONSABLE_FILTER_COLUMN,
    ...LEGACY_RESPONSABLE_COLUMNS,
    DOSSIER_STATUS_FILTER_COLUMN
  ]

  return withDefaultDossierStatusFilter(
    filters.filter((entry) => !panelColumns.includes(entry.column)),
    dashboard
  )
}

/**
 * A view state persisted before the status filter existed carries no status entry:
 * re-inject the predefined default instead of silently including prospects.
 * An explicit entry — even an empty "include everything" one — is kept as is.
 */
export function withDefaultDossierStatusFilter(
  filters: AnalyticsFilter[] | null | undefined,
  dashboard: FilterableDashboard | null | undefined
): AnalyticsFilter[] {
  const entries = [...(filters ?? [])]

  if (
    !dashboard?.is_default ||
    entries.some((entry) => entry.column === DOSSIER_STATUS_FILTER_COLUMN)
  ) {
    return entries
  }

  return [...entries, defaultDossierStatusFilter()]
}

/**
 * Dossier statuses a detail page excludes. The dashboard syncs its selection into the
 * store; a detail page opened directly (URL, refresh) finds the store never synced
 * (null) and must still apply the predefined default instead of including everything.
 */
export function resolveExcludedDossierStatuses(
  syncedStatuses: string[] | null | undefined,
  dashboard: FilterableDashboard | null | undefined
): string[] {
  if (Array.isArray(syncedStatuses)) {
    return syncedStatuses
  }

  return dashboard?.is_default ? [...PREDEFINED_EXCLUDED_DOSSIER_STATUSES] : []
}
